
import { format } from 'date-fns';
import { formatCurrency, formatNumber as formatNumberUtil } from '@/utils/formatters';

export interface LocalTransaction {
  id: string;
  accountId?: string;
  account_id?: string;
  type: 'income' | 'expense' | 'transfer';
  amount: number;
  date: Date;
  description?: string;
  title?: string;
  category?: string;
  categoryId?: string;
  category_id?: string;
  payee?: string;
  payeeId?: string;
  payee_id?: string;
  userId?: string;
  user_id?: string;
  createdAt?: string | Date;
  created_at?: string;
  updatedAt?: string | Date;
  updated_at?: string;
  transferDetails?: {
    direction: 'in' | 'out';
    fromAccountId: string;
    toAccountId: string;
    fromAmount: number;
    toAmount: number;
    exchangeRate: number;
    fromAccountName?: string;
    toAccountName?: string;
  };
  _categoryDisplayName?: string;
  _accountDisplayName?: string;
  status: 'pending' | 'completed' | 'cancelled';
  notes?: string;
  tags?: string[];
  attachments?: any[];
  account?: any;
  currencyId?: string;
  hasAttachment?: boolean;
}

/**
 * Format currency amount with proper styling
 */
export const formatCurrencyAmount = (amount: number, currencyCode: string) => {
  try {
    return formatCurrency(amount, currencyCode);
  } catch (error) {
    console.warn('Error formatting currency:', error);
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
};

/**
 * Format number with proper locale
 */
export const formatNumber = (value: number): string => {
  try {
    return formatNumberUtil(value);
  } catch (error) {
    console.warn('Error formatting number:', error);
    return value.toString();
  }
};

/**
 * Format date for transaction display with time using user preferences
 */
export const formatTransactionDate = (transaction: LocalTransaction): string => {
  try {
    if (!transaction.date) return '';
    
    const date = transaction.date instanceof Date ? transaction.date : new Date(transaction.date);
    
    // Use formatDateTime from dateUtils which respects user preferences
    try {
      const { formatDateTime } = require('@/lib/dateUtils');
      return formatDateTime(date);
    } catch {
      // Fallback to default format with time
      return format(date, 'MMM dd, yyyy HH:mm');
    }
  } catch (error) {
    console.warn('Error formatting date:', error);
    return '';
  }
};

// Currency symbol mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  TRY: '₺',
  INR: '₹',
  CNY: '¥',
  RUB: '₽',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'CHF',
  SEK: 'kr',
  NOK: 'kr',
  PLN: 'zł',
  BRL: 'R$',
  MXN: '$',
  ZAR: 'R',
  KRW: '₩',
  SGD: 'S$',
  HKD: 'HK$',
  NZD: 'NZ$',
};

/**
 * Format amount with color based on transaction type using currency symbol
 */
export const formatAmountWithColor = (amount: number, currencyCode: string): JSX.Element => {
  const isPositive = amount > 0;
  const displayAmount = Math.abs(amount);
  
  // Get currency symbol from mapping, fallback to code
  const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;
  
  // Format number with user preferences if available
  let formattedNumber: string;
  try {
    const { formatNumber } = require('@/utils/formatters');
    formattedNumber = formatNumber(displayAmount);
  } catch {
    // Fallback to basic formatting
    formattedNumber = displayAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
  
  return (
    <span className={isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
      {isPositive ? '+' : '-'}{symbol}{formattedNumber}
    </span>
  );
};

/**
 * Get display value for transaction field
 */
export const getTransactionDisplayValue = (
  transaction: LocalTransaction, 
  field: string
): string => {
  switch (field) {
    case 'date':
      return formatTransactionDate(transaction);
    case 'amount':
      return formatCurrencyAmount(transaction.amount, transaction.currencyId || 'USD');
    case 'description':
      return transaction.description || transaction.title || '-';
    case 'notes':
      return transaction.notes || '';
    case 'category':
      return transaction._categoryDisplayName || 'Uncategorized';
    case 'account':
      return transaction._accountDisplayName || 'Unknown Account';
    case 'payee':
      return transaction.payee || '';
    default:
      return '';
  }
}; 